import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  MessageSquare, 
  Bug, 
  Lightbulb, 
  Star,
  Send,
  X,
  ChevronUp,
  Sparkles
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

// Beta Badge Component - Shows ONLY on the landing page (/)
// Positioned below header on desktop, hidden on mobile to avoid overlap with profile
export const BetaBadge = () => {
  // Must be inside BrowserRouter to use this - the component in App.js wraps it properly
  return null; // Disabled globally - will be shown via LandingPageBetaBadge in App.js
};

// Floating Feedback Button
export const BetaFeedbackWidget = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("feedback");
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  
  // Form states
  const [feedbackForm, setFeedbackForm] = useState({
    rating: "",
    easeOfUse: "",
    wouldRecommend: "",
    favoriteFeature: "",
    improvementSuggestion: "",
    additionalComments: ""
  });
  
  const [bugForm, setBugForm] = useState({
    page: "",
    description: "",
    steps: "",
    severity: "medium"
  });
  
  const [featureForm, setFeatureForm] = useState({
    title: "",
    description: "",
    priority: "nice_to_have"
  });

  const handleSubmitFeedback = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/beta/feedback`, {
        type: "survey",
        user_id: user?.user_id,
        user_email: user?.email,
        user_role: user?.role,
        data: feedbackForm,
        submitted_at: new Date().toISOString()
      });
      toast.success("Thank you for your feedback! 🙏");
      setFeedbackForm({
        rating: "",
        easeOfUse: "",
        wouldRecommend: "",
        favoriteFeature: "",
        improvementSuggestion: "",
        additionalComments: ""
      });
      setIsOpen(false);
    } catch (error) {
      // Store locally if API fails
      const localFeedback = JSON.parse(localStorage.getItem('beta_feedback') || '[]');
      localFeedback.push({ type: "survey", data: feedbackForm, timestamp: Date.now() });
      localStorage.setItem('beta_feedback', JSON.stringify(localFeedback));
      toast.success("Feedback saved! Thank you! 🙏");
      setIsOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitBug = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/beta/feedback`, {
        type: "bug_report",
        user_id: user?.user_id,
        user_email: user?.email,
        user_role: user?.role,
        data: bugForm,
        submitted_at: new Date().toISOString()
      });
      toast.success("Bug report submitted! We'll fix it ASAP 🔧");
      setBugForm({ page: "", description: "", steps: "", severity: "medium" });
      setIsOpen(false);
    } catch (error) {
      const localFeedback = JSON.parse(localStorage.getItem('beta_feedback') || '[]');
      localFeedback.push({ type: "bug_report", data: bugForm, timestamp: Date.now() });
      localStorage.setItem('beta_feedback', JSON.stringify(localFeedback));
      toast.success("Bug report saved! Thank you! 🔧");
      setIsOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFeature = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/beta/feedback`, {
        type: "feature_request",
        user_id: user?.user_id,
        user_email: user?.email,
        user_role: user?.role,
        data: featureForm,
        submitted_at: new Date().toISOString()
      });
      toast.success("Feature request submitted! Great idea! 💡");
      setFeatureForm({ title: "", description: "", priority: "nice_to_have" });
      setIsOpen(false);
    } catch (error) {
      const localFeedback = JSON.parse(localStorage.getItem('beta_feedback') || '[]');
      localFeedback.push({ type: "feature_request", data: featureForm, timestamp: Date.now() });
      localStorage.setItem('beta_feedback', JSON.stringify(localFeedback));
      toast.success("Feature request saved! Great idea! 💡");
      setIsOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
        {/* Expanded Options */}
        {expanded && (
          <div className="flex flex-col gap-2 animate-in slide-in-from-bottom-2">
            <Button
              onClick={() => { setActiveTab("feedback"); setIsOpen(true); setExpanded(false); }}
              className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black shadow-lg hover:shadow-xl"
            >
              <Star className="w-4 h-4 mr-2" />
              Give Feedback
            </Button>
            <Button
              onClick={() => { setActiveTab("bug"); setIsOpen(true); setExpanded(false); }}
              className="bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg hover:shadow-xl"
            >
              <Bug className="w-4 h-4 mr-2" />
              Report a Bug
            </Button>
            <Button
              onClick={() => { setActiveTab("feature"); setIsOpen(true); setExpanded(false); }}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg hover:shadow-xl"
            >
              <Lightbulb className="w-4 h-4 mr-2" />
              Request Feature
            </Button>
          </div>
        )}
        
        {/* Main Toggle Button */}
        <Button
          onClick={() => setExpanded(!expanded)}
          className="h-14 w-14 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg hover:shadow-xl transition-all"
        >
          {expanded ? (
            <X className="w-6 h-6" />
          ) : (
            <MessageSquare className="w-6 h-6" />
          )}
        </Button>
      </div>

      {/* Feedback Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="bg-stone-900 border-stone-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" />
              Beta Feedback
              <Badge className="bg-purple-600 text-white text-xs">BETA TESTER</Badge>
            </DialogTitle>
          </DialogHeader>

          {/* Tab Buttons */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={activeTab === "feedback" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("feedback")}
              className={activeTab === "feedback" ? "bg-amber-500 text-black" : "border-stone-600 text-stone-300"}
            >
              <Star className="w-4 h-4 mr-1" />
              Survey
            </Button>
            <Button
              variant={activeTab === "bug" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("bug")}
              className={activeTab === "bug" ? "bg-red-500 text-white" : "border-stone-600 text-stone-300"}
            >
              <Bug className="w-4 h-4 mr-1" />
              Bug
            </Button>
            <Button
              variant={activeTab === "feature" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("feature")}
              className={activeTab === "feature" ? "bg-blue-500 text-white" : "border-stone-600 text-stone-300"}
            >
              <Lightbulb className="w-4 h-4 mr-1" />
              Feature
            </Button>
          </div>

          {/* Feedback Survey Form */}
          {activeTab === "feedback" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-stone-300">Overall Rating *</Label>
                <RadioGroup
                  value={feedbackForm.rating}
                  onValueChange={(v) => setFeedbackForm({...feedbackForm, rating: v})}
                  className="flex gap-2"
                >
                  {["1", "2", "3", "4", "5"].map((num) => (
                    <div key={num} className="flex flex-col items-center">
                      <RadioGroupItem value={num} id={`rating-${num}`} className="sr-only" />
                      <Label
                        htmlFor={`rating-${num}`}
                        className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer border-2 transition-all ${
                          feedbackForm.rating === num 
                            ? "bg-amber-500 border-amber-500 text-black" 
                            : "border-stone-600 text-stone-400 hover:border-amber-500/50"
                        }`}
                      >
                        {num}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                <p className="text-xs text-stone-500">1 = Poor, 5 = Excellent</p>
              </div>

              <div className="space-y-2">
                <Label className="text-stone-300">How easy was the app to use?</Label>
                <Select
                  value={feedbackForm.easeOfUse}
                  onValueChange={(v) => setFeedbackForm({...feedbackForm, easeOfUse: v})}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="very_easy">Very Easy</SelectItem>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="neutral">Neutral</SelectItem>
                    <SelectItem value="difficult">Difficult</SelectItem>
                    <SelectItem value="very_difficult">Very Difficult</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-stone-300">Would you recommend MyHairChair?</Label>
                <Select
                  value={feedbackForm.wouldRecommend}
                  onValueChange={(v) => setFeedbackForm({...feedbackForm, wouldRecommend: v})}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="definitely">Definitely Yes</SelectItem>
                    <SelectItem value="probably">Probably Yes</SelectItem>
                    <SelectItem value="not_sure">Not Sure</SelectItem>
                    <SelectItem value="probably_not">Probably Not</SelectItem>
                    <SelectItem value="definitely_not">Definitely Not</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-stone-300">Favorite Feature?</Label>
                <Input
                  placeholder="e.g., Demo Mode, SMS notifications, etc."
                  value={feedbackForm.favoriteFeature}
                  onChange={(e) => setFeedbackForm({...feedbackForm, favoriteFeature: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-stone-300">What could we improve?</Label>
                <Textarea
                  placeholder="Tell us what would make MyHairChair even better..."
                  value={feedbackForm.improvementSuggestion}
                  onChange={(e) => setFeedbackForm({...feedbackForm, improvementSuggestion: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white min-h-[80px]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-stone-300">Additional Comments</Label>
                <Textarea
                  placeholder="Any other thoughts..."
                  value={feedbackForm.additionalComments}
                  onChange={(e) => setFeedbackForm({...feedbackForm, additionalComments: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white min-h-[60px]"
                />
              </div>

              <Button
                onClick={handleSubmitFeedback}
                disabled={submitting || !feedbackForm.rating}
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-black"
              >
                {submitting ? "Submitting..." : "Submit Feedback"}
                <Send className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Bug Report Form */}
          {activeTab === "bug" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-stone-300">Which page has the bug? *</Label>
                <Select
                  value={bugForm.page}
                  onValueChange={(v) => setBugForm({...bugForm, page: v})}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue placeholder="Select page..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home">Home / Landing Page</SelectItem>
                    <SelectItem value="login">Login</SelectItem>
                    <SelectItem value="register">Registration</SelectItem>
                    <SelectItem value="dashboard">Dashboard</SelectItem>
                    <SelectItem value="booking">Booking Page</SelectItem>
                    <SelectItem value="find_stylists">Find Stylists</SelectItem>
                    <SelectItem value="profile">Profile Settings</SelectItem>
                    <SelectItem value="gallery">Photo Gallery</SelectItem>
                    <SelectItem value="membership">Membership</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-stone-300">Describe the bug *</Label>
                <Textarea
                  placeholder="What went wrong? What did you expect to happen?"
                  value={bugForm.description}
                  onChange={(e) => setBugForm({...bugForm, description: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white min-h-[80px]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-stone-300">Steps to reproduce</Label>
                <Textarea
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                  value={bugForm.steps}
                  onChange={(e) => setBugForm({...bugForm, steps: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white min-h-[80px]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-stone-300">Severity</Label>
                <Select
                  value={bugForm.severity}
                  onValueChange={(v) => setBugForm({...bugForm, severity: v})}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low - Minor issue</SelectItem>
                    <SelectItem value="medium">Medium - Affects usability</SelectItem>
                    <SelectItem value="high">High - Blocks functionality</SelectItem>
                    <SelectItem value="critical">Critical - App crashes</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleSubmitBug}
                disabled={submitting || !bugForm.page || !bugForm.description}
                className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white"
              >
                {submitting ? "Submitting..." : "Submit Bug Report"}
                <Bug className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Feature Request Form */}
          {activeTab === "feature" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-stone-300">Feature Title *</Label>
                <Input
                  placeholder="e.g., Dark mode, Calendar sync, etc."
                  value={featureForm.title}
                  onChange={(e) => setFeatureForm({...featureForm, title: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-stone-300">Describe the feature *</Label>
                <Textarea
                  placeholder="What would this feature do? How would it help you?"
                  value={featureForm.description}
                  onChange={(e) => setFeatureForm({...featureForm, description: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-stone-300">Priority to you</Label>
                <Select
                  value={featureForm.priority}
                  onValueChange={(v) => setFeatureForm({...featureForm, priority: v})}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nice_to_have">Nice to have</SelectItem>
                    <SelectItem value="important">Important</SelectItem>
                    <SelectItem value="critical">Critical - I need this!</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleSubmitFeature}
                disabled={submitting || !featureForm.title || !featureForm.description}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
              >
                {submitting ? "Submitting..." : "Submit Feature Request"}
                <Lightbulb className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BetaFeedbackWidget;
